import axios from "axios";
import config from "../config";
import { KYB_BOOKING_ERROR_CODES } from "../utils/kyb";



/**
 * ============================================================================
 * Auth-bearing requests now ride on httpOnly cookies (Task #99) instead
 * of a bearer token pulled from localStorage — `withCredentials: true` is
 * what makes the browser attach/accept those cookies cross-origin (the
 * backend's CORS config already allow-lists specific origins with
 * `credentials: true`, which cookie auth requires — it cannot pair with a
 * wildcard origin).
 *
 * The CSRF token cookie is deliberately NOT httpOnly (see backend
 * utils/authCookies.js) so it can be read here and echoed back as the
 * `x-csrf-token` header on state-changing session calls (double-submit
 * pattern) — this is what stops a cross-site page from riding the user's
 * cookies to call logout/refresh on their behalf.
 * ============================================================================
 */

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export const httpinstance = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-Client-Type": "WEB",
  },
});

httpinstance.interceptors.request.use(async (request) => {
  const csrfToken = readCookie("csrf_token");
  if (csrfToken) request.headers["x-csrf-token"] = csrfToken;
  return request;
});

// A 401 from this API does NOT reliably mean "your session is dead". Several
// endpoints reuse 401 for business-logic rejections — e.g. single_booking_data
// answers "You are not authorized for this data" when the payment record simply
// isn't found. Role rejections ("Unauthorized access" from checkAdmin/checkKam)
// are likewise deliberately excluded: the session is valid there, the role
// just lacks access, so it must not trigger a refresh/logout cycle.
const SESSION_FAILURE_MESSAGES = [
  "access denied: no token provided",
  "invalid or expired token",
];

const isSessionExpired = (error) => {
  const message = error?.response?.data?.message;
  return typeof message === "string" && SESSION_FAILURE_MESSAGES.includes(message.trim().toLowerCase());
};

let refreshPromise = null;

/**
 * Single-flight refresh: however many requests hit an expired session at
 * once, only one POST /auth/sessions/refresh is ever in flight — every
 * caller awaits the same promise (Task #105).
 */
// function refreshSession() {
//   if (!refreshPromise) {
//     refreshPromise = httpinstance
//       .post("auth/sessions/refresh")
//       .finally(() => {
//         refreshPromise = null;
//       });
//   }
//   return refreshPromise;
// }
export function refreshSession() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = httpinstance
    .post("auth/sessions/refresh", {}, {
      withCredentials: true, // ✅ sends refresh_token cookie cross-site
    })
    .then((response) => {
      return response.data; // return the new tokens/session data to caller
    })
    .catch((error) => {
      throw error; // re-throw so the original caller can handle it too
    })
    .finally(() => {
      refreshPromise = null; // clear singleton after settle
    });

  return refreshPromise;
}

// function handleSessionExpired() {
//   // Clear any locally stored auth state
//   //localStorage.removeItem("access_token"); // adjust key to match yours
//   // Redirect to login
//   //window.location.href = "/login";
//   window.location.replace(`${window.location.origin}/auth/login`);
// }

export const endSession = () => {
  if (typeof window === "undefined") return;
  window.location.replace(`${window.location.origin}/auth/login`);
};

// GET auth/session is a deliberate "am I logged in?" probe — checkSession()
// (AuthSlice.js) already treats a failure here as "not authenticated" and
// its callers (ProtectedRoute, layouts/Login.jsx) already redirect with a
// normal React Router `navigate()`. Without this guard, the very first
// visit to /auth/login (no access or refresh cookie at all — Task
// "loading the login page first time") would: 401 on this probe, attempt a
// refresh, have that refresh also fail (no refresh cookie either), and then
// call endSession() below — a hard `window.location.replace` back to
// /auth/login, i.e. the page already on screen, reloading it and repeating
// the exact same sequence forever. Every other protected call still gets
// the hard redirect+reload on a failed refresh, since there the app may be
// showing private content that must not stay visible.
const normalizeUrl = (url) => (typeof url === "string" ? url.replace(/^\//, "") : url);
const isSessionProbeRequest = (config) => normalizeUrl(config?.url) === "auth/session";

// The refresh call itself must never be retried through this same
// refresh-on-401 path. If `auth/sessions/refresh` ever answers with a 401
// whose message happens to match SESSION_FAILURE_MESSAGES (e.g. an expired
// refresh cookie), treating that as "session expired, go refresh" would
// call refreshSession() again from inside the interceptor handling for that
// very refresh request — which, since refreshSession() is single-flight,
// means awaiting the still-in-flight promise for the request that hasn't
// finished failing yet. That either hangs (the .finally() that clears
// refreshPromise is chained onto this very call) or churns into a refresh
// loop. Excluding the refresh endpoint here means its own failures just
// reject normally, and the *caller* of refreshSession() (below) is what
// decides to end the session.
const isRefreshRequest = (config) => normalizeUrl(config?.url) === "auth/sessions/refresh";

httpinstance.interceptors.response.use(
  async (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;

    if (
      status === 401 &&
      isSessionExpired(error) &&
      originalRequest &&
      !originalRequest._retriedAfterRefresh &&
      !isRefreshRequest(originalRequest)
    ) {
      originalRequest._retriedAfterRefresh = true;
      try {
        await refreshSession();
        return httpinstance(originalRequest);
      } catch (refreshError) {
        if (!isSessionProbeRequest(originalRequest)) {
          endSession();
        }
        return Promise.reject(error);
      }
    }

    if (status === 402) {
      endSession();
      return Promise.reject(error);
    }

    // A booking refused because business verification isn't approved (the
    // status may have changed since the page loaded): have PageLayout reload
    // it so the ribbon and the Book buttons catch up.
    if (status === 403 && KYB_BOOKING_ERROR_CODES.includes(error?.response?.data?.code)) {
      window.dispatchEvent(new Event("kyb:status-changed"));
    }

    return Promise.reject(error);
  },
);

export const httpinstancefile = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: true,
  headers: { "Content-Type": "multipart/form-data" },
});

httpinstancefile.interceptors.request.use(async (request) => {
  const csrfToken = readCookie("csrf_token");
  if (csrfToken) request.headers["x-csrf-token"] = csrfToken;
  return request;
});

httpinstancefile.interceptors.response.use(
  async (response) => response,
  async (error) => {
    const originalConfig = error.config;
    if (originalConfig?.url !== "/auth/login" && error.response) {
      switch (error.response.data?.statusCode) {
        case 402:
          endSession();
          break;
        default:
          return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);
